// Koneksi AI (/koneksi-ai) — who may use the MCP cloud relay for this account
// (DECISIONS D45, docs/API.md §12.4): AI clients connected through the consent
// page (OAuth grants), static API keys and the computers that dialled the relay, each
// revocable, plus one lever that revokes all of it. The page manages ACCESS
// only: it never runs a tool call and never shows a call's content (D42).
//
// States: while the server's relay switch is off every /api/v1/mcp route
// answers 404, which renders a calm "not available yet" card, never an error;
// an API without GET /mcp/relay/devices gets the same treatment for that list
// alone. Each list settles on its own (lib/mcp.ts loadMcpAccess), so one failure
// never blanks the other two.
//
// A new key's secret lives in this component's state only: shown once, never
// written anywhere, dropped on "Selesai", when that key or everything is
// revoked, and when the page unmounts.
//
// Card anatomy: shadcn-ui/ui apps/v4/registry/new-york-v4/ui/card.tsx — MIT;
// rows follow the stacked /purchases list (ui/DESIGN.md §5).

import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Glyph } from "@/ui";
import { dataClass } from "@ahcut/ui/recipes";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  Select,
} from "@/ui";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState, ErrorState, Loading } from "@/components/States";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CopyButton } from "@/components/CopyButton";
import { cn } from "@/lib/cn";
import { useAsync } from "@/lib/useAsync";
import { ApiError } from "@/lib/api";
import { toError } from "@/lib/errors";
import { MARKETING_BASE } from "@/lib/config";
import { formatDate, formatDateTime } from "@/lib/format";
import {
  MCP_KEY_EXPIRY_OPTIONS,
  MCP_KEY_NAME_MAX,
  MCP_RELAY_URL,
  createMcpKey,
  isMcpKeyExpired,
  keyCreateError,
  loadMcpAccess,
  maskKeyPrefix,
  mcpClientIdLabel,
  mcpClientLabel,
  revokeAllMcpAccess,
  revokeMcpGrant,
  revokeMcpKey,
  revokeMcpRelayDevice,
  shortDeviceId,
  type McpAccess,
  type Settled,
} from "@/lib/mcp";
import type { McpCreatedKey, McpGrant, McpKey, McpRelayDevice } from "@/lib/types";

const PAGE_TITLE = "Koneksi AI";
const RELAY_HOST = new URL(MCP_RELAY_URL).host;
/** The key-create form's error, referenced by the name input (aria-describedby). */
const CREATE_ERROR_ID = "koneksi-ai-key-error";

type AvailableAccess = Extract<McpAccess, { available: true }>;

/** One revoke waiting for confirmation. A single ConfirmDialog serves every
 *  list and the revoke-all lever, so busy/error handling exists once. */
interface PendingRevoke {
  title: string;
  body: ReactNode;
  confirmLabel: string;
  /** Shown (and announced) once the revoke succeeded. */
  done: string;
  run: () => Promise<void>;
  /** The key this revokes, so a secret still on screen for it is dropped. */
  keyId?: string;
  /** Revokes everything, the secret on screen included. */
  all?: boolean;
}

type OnRevoke = (request: PendingRevoke) => void;

export function AiConnections() {
  const {
    data: access,
    loading,
    error,
    reload,
  } = useAsync<McpAccess>((signal) => loadMcpAccess(signal), []);
  const [created, setCreated] = useState<McpCreatedKey | null>(null);
  const [pending, setPending] = useState<PendingRevoke | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<Error | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function askRevoke(request: PendingRevoke) {
    setActionError(null);
    setPending(request);
  }

  async function confirmRevoke() {
    if (!pending) return;
    setBusy(true);
    setActionError(null);
    try {
      await pending.run();
      if (pending.all || (pending.keyId !== undefined && created?.id === pending.keyId)) {
        setCreated(null);
      }
      setNotice(pending.done);
      setPending(null);
      reload();
    } catch (err) {
      // The dialog stays open with the reason; nothing was revoked.
      setActionError(toError(err));
    } finally {
      setBusy(false);
    }
  }

  // Rendered in every state so the screen always has its h1 (ui/DESIGN.md §10).
  const header = (
    <>
      <PageHeader
        title={PAGE_TITLE}
        lede="Siapa saja yang bisa memakai ahcut di komputermu lewat relay MCP, dan tempat mencabut aksesnya."
      />
      <Intro />
    </>
  );

  // Full-page states only while there is nothing to show yet: a reload after a
  // revoke or a new key keeps the lists (and the new key's secret) on screen.
  if (loading && !access) {
    return (
      <>
        {header}
        <Loading label="Memuat koneksi AI…" />
      </>
    );
  }
  if (error || !access) {
    return (
      <>
        {header}
        <ErrorState error={error ?? new Error("Data koneksi AI tidak tersedia.")} onRetry={reload} />
      </>
    );
  }
  if (!access.available) {
    return (
      <>
        {header}
        <EmptyState title="Koneksi AI belum tersedia">
          Server ini belum menyediakan relay MCP, jadi belum ada klien, kunci API, atau komputer yang bisa
          dikelola di sini.
        </EmptyState>
      </>
    );
  }

  // Fragment: the shell's content column (Layout app-main-inner) sets the rhythm.
  return (
    <>
      {header}
      {notice && (
        <Alert tone="ok" role="status">
          {notice}
        </Alert>
      )}
      <ClientsCard grants={access.grants} onRevoke={askRevoke} onRetry={reload} />
      <KeysCard
        keys={access.keys}
        created={created}
        onCreated={(key) => {
          setNotice(null);
          setCreated(key);
          reload();
        }}
        onDismissCreated={() => setCreated(null)}
        onRevoke={askRevoke}
        onRetry={reload}
      />
      <DevicesCard devices={access.devices} onRevoke={askRevoke} onRetry={reload} />
      <RevokeAllCard access={access} onRevoke={askRevoke} />

      <ConfirmDialog
        open={pending !== null}
        title={pending?.title ?? ""}
        confirmLabel={pending?.confirmLabel ?? ""}
        danger
        busy={busy}
        onConfirm={confirmRevoke}
        onCancel={() => setPending(null)}
      >
        {pending?.body}
        {actionError && <ErrorState variant="inline" error={actionError} live />}
      </ConfirmDialog>
    </>
  );
}

/** What the relay is, in the words /mcp and /privasi already use. */
function Intro() {
  return (
    <div className="flex max-w-[60ch] flex-col gap-3 text-fg-1">
      <p>
        Relay MCP menyambungkan asisten AI yang bekerja dari cloud, seperti claude.ai, ke ahcut di
        komputermu. ahcut yang menelepon keluar ke relay di{" "}
        <code className="whitespace-nowrap">{RELAY_HOST}</code> — tidak ada port
        yang dibuka di komputermu — dan relay hanya aktif kalau kamu menyalakannya sendiri di
        aplikasi.
      </p>
      <p>
        Isi panggilan lewat server kami hanya selama panggilan berjalan dan tidak pernah disimpan;
        yang dicatat hanya metadatanya. Halaman ini mengatur akses saja: ia tidak menjalankan
        panggilan dan tidak menampilkan isinya.
      </p>
      <p className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
        <a href={`${MARKETING_BASE}/mcp`} target="_blank" rel="noopener noreferrer">
          Apa yang bisa dikerjakan asisten
        </a>
        <a href={`${MARKETING_BASE}/privasi`} target="_blank" rel="noopener noreferrer">
          Data yang dicatat relay
        </a>
      </p>
    </div>
  );
}

// ---------------------------------------------------------------- Clients

function ClientsCard({
  grants,
  onRevoke,
  onRetry,
}: {
  grants: Settled<McpGrant[]>;
  onRevoke: OnRevoke;
  onRetry: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Klien yang terhubung</CardTitle>
        <CardDescription>
          Asisten AI yang kamu izinkan lewat halaman persetujuan ahcut. Nama klien ditulis oleh
          kliennya sendiri; label Terverifikasi hanya dipasang oleh ahcut.
        </CardDescription>
      </CardHeader>
      <SettledList
        list={grants}
        label="Klien yang terhubung"
        empty="Belum ada klien yang terhubung."
        onRetry={onRetry}
        renderItem={(grant) => {
          const name = mcpClientLabel(grant);
          const clientId = mcpClientIdLabel(grant.client_id);
          const facts: Fact[] = [
            { term: "Terhubung sejak", value: formatDate(grant.created_at), data: true },
            lastUsedFact(grant.last_used_at),
          ];
          // A client that declared no name is already shown by its id.
          if (clientId !== name) facts.unshift({ term: "ID klien", value: clientId, data: true });
          return (
            <AccessRow
              key={grant.id}
              title={name}
              badge={
                grant.verified ? (
                  <Badge tone="ok">Terverifikasi</Badge>
                ) : (
                  <Badge>Belum terverifikasi</Badge>
                )
              }
              facts={facts}
              action={
                <RevokeButton
                  label={`Cabut akses ${name}`}
                  onClick={() =>
                    onRevoke({
                      title: "Cabut akses klien ini?",
                      body: (
                        <p>
                          <strong className="font-medium text-fg-0">{name}</strong> tidak bisa lagi
                          memakai ahcut lewat relay. Untuk tersambung lagi, klien itu harus meminta
                          izinmu lagi.
                        </p>
                      ),
                      confirmLabel: "Cabut akses",
                      done: `Akses ${name} dicabut.`,
                      run: () => revokeMcpGrant(grant.id),
                    })
                  }
                />
              }
            />
          );
        }}
      />
    </Card>
  );
}

// ---------------------------------------------------------------- API keys

function KeysCard({
  keys,
  created,
  onCreated,
  onDismissCreated,
  onRevoke,
  onRetry,
}: {
  keys: Settled<McpKey[]>;
  created: McpCreatedKey | null;
  onCreated: (key: McpCreatedKey) => void;
  onDismissCreated: () => void;
  onRevoke: OnRevoke;
  onRetry: () => void;
}) {
  const [name, setName] = useState("");
  /** "" = never expires; otherwise a day count, as the select's option value. */
  const [expiry, setExpiry] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const trimmed = name.trim();
  // Only a refused NAME marks the input invalid; a limit or a server failure is
  // described on it without blaming what was typed.
  const nameRefused = error instanceof ApiError && error.code === "validation";

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      const key = await createMcpKey(trimmed, expiry === "" ? null : Number(expiry));
      setName("");
      onCreated(key);
    } catch (err) {
      setError(keyCreateError(err));
    } finally {
      setBusy(false);
    }
  }

  function dismissCreated() {
    onDismissCreated();
    nameRef.current?.focus();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Kunci API</CardTitle>
        <CardDescription>
          Untuk klien atau skrip yang tidak bisa tersambung lewat halaman persetujuan. Pemegang
          kunci bisa memakai relay atas nama akunmu, jadi perlakukan seperti kata sandi.
        </CardDescription>
      </CardHeader>

      <form onSubmit={create} className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <Field
            ref={nameRef}
            className="flex-1"
            label="Nama kunci"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={MCP_KEY_NAME_MAX}
            placeholder="mis. skrip otomasi"
            autoComplete="off"
            spellCheck={false}
            aria-describedby={error ? CREATE_ERROR_ID : undefined}
            aria-invalid={nameRefused || undefined}
          />
          <Field label="Masa berlaku" className="sm:w-52">
            {(control) => (
              <Select {...control} value={expiry} onChange={(e) => setExpiry(e.target.value)}>
                {MCP_KEY_EXPIRY_OPTIONS.map((option) => (
                  <option key={option.label} value={option.days === null ? "" : String(option.days)}>
                    {option.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Button type="submit" variant="secondary" disabled={!trimmed || busy}>
            {busy ? "Membuat…" : "Buat kunci"}
          </Button>
        </div>
        {error && <ErrorState variant="inline" id={CREATE_ERROR_ID} error={error} live />}
      </form>

      {created && <CreatedKeyPanel created={created} onDismiss={dismissCreated} />}

      <SettledList
        list={keys}
        label="Kunci API"
        empty="Belum ada kunci API."
        onRetry={onRetry}
        renderItem={(key) => {
          const { mask, tail } = maskKeyPrefix(key.key_prefix);
          return (
            <AccessRow
              key={key.id}
              title={key.name}
              badge={isMcpKeyExpired(key) ? <Badge>Kedaluwarsa</Badge> : undefined}
              detail={
                <p className={cn(dataClass, "text-sm text-fg-2")}>
                  <span className="sr-only">Kunci berakhiran </span>
                  <span aria-hidden="true">{mask}</span>
                  {tail}
                </p>
              }
              facts={[
                { term: "Dibuat", value: formatDate(key.created_at), data: true },
                key.expires_at
                  ? { term: "Berlaku sampai", value: formatDate(key.expires_at), data: true }
                  : { term: "Berlaku sampai", value: "tidak kedaluwarsa" },
                lastUsedFact(key.last_used_at),
              ]}
              action={
                <RevokeButton
                  label={`Cabut kunci ${key.name}`}
                  onClick={() =>
                    onRevoke({
                      title: "Cabut kunci API ini?",
                      body: (
                        <p>
                          Kunci <strong className="font-medium text-fg-0">{key.name}</strong>{" "}
                          berhenti bekerja: klien atau skrip yang memakainya kehilangan akses ke
                          relay. Kunci yang dicabut tidak bisa dipulihkan; kalau masih perlu, buat
                          kunci baru.
                        </p>
                      ),
                      confirmLabel: "Cabut kunci",
                      done: `Kunci ${key.name} dicabut.`,
                      run: () => revokeMcpKey(key.id),
                      keyId: key.id,
                    })
                  }
                />
              }
            />
          );
        }}
      />
    </Card>
  );
}

/** The one and only showing of a new key's secret (POST /mcp/keys is the only
 *  response that carries it; the API keeps a hash). */
function CreatedKeyPanel({ created, onDismiss }: { created: McpCreatedKey; onDismiss: () => void }) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  // Focus moves to the secret the moment it appears, so a keyboard or
  // screen-reader user meets the "only shown now" warning before anything else.
  useEffect(() => {
    panelRef.current?.focus();
  }, [created.id]);

  return (
    <Alert
      ref={panelRef}
      tabIndex={-1}
      role="group"
      aria-labelledby={titleId}
      tone="warn"
      title={<span id={titleId}>Salin kunci ini sekarang</span>}
      className="focus:outline-none"
    >
      <div className="flex flex-col gap-3">
        <p>
          Kunci <strong className="font-medium text-fg-0">{created.name}</strong> hanya ditampilkan
          sekali, di sini. ahcut tidak menyimpan kuncinya, jadi tidak bisa menampilkannya lagi —
          kalau hilang, cabut kunci ini lalu buat yang baru.
        </p>
        <div
          role="group"
          aria-label="Kunci API baru"
          className={cn(
            dataClass,
            "rounded-md border border-line bg-surface-0 px-3 py-2.5 leading-6 break-all text-fg-0 select-all",
          )}
        >
          {created.key}
        </div>
        <div className="flex flex-wrap gap-3">
          <CopyButton value={created.key} label="Salin kunci" />
          <Button variant="quiet" onClick={onDismiss}>
            Selesai
          </Button>
        </div>
        {/* nowrap: the inline-code base style breaks anywhere, which split the URL
            mid-word; both tokens fit one line even at 360px. */}
        <p>
          Klien mengirim kunci ini sebagai header{" "}
          <code className="whitespace-nowrap">Authorization: Bearer …</code> ke{" "}
          <code className="whitespace-nowrap">{MCP_RELAY_URL}</code>.
        </p>
      </div>
    </Alert>
  );
}

// ---------------------------------------------------------------- Computers

function DevicesCard({
  devices,
  onRevoke,
  onRetry,
}: {
  /** null: this server has no device list yet (lib/mcp.ts McpAccess). */
  devices: Settled<McpRelayDevice[]> | null;
  onRevoke: OnRevoke;
  onRetry: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Komputer yang tersambung ke relay</CardTitle>
        <CardDescription>Komputer tempat ahcut tersambung ke relay atas nama akunmu.</CardDescription>
      </CardHeader>
      {devices === null ? (
        <p className="text-sm text-fg-2">Daftar komputer belum tersedia di server ini.</p>
      ) : (
        <>
          <SettledList
            list={devices}
            label="Komputer yang tersambung ke relay"
            empty="Belum ada komputer yang tersambung ke relay."
            onRetry={onRetry}
            renderItem={(device) => {
              const short = shortDeviceId(device.device_id);
              const revoked = device.revoked_at !== null;
              return (
                <AccessRow
                  key={device.device_id}
                  title={
                    <>
                      Komputer <span className={dataClass}>{short}</span>
                    </>
                  }
                  badge={revoked ? <Badge>Dicabut</Badge> : undefined}
                  facts={[
                    device.app_version
                      ? { term: "Versi aplikasi", value: device.app_version, data: true }
                      : { term: "Versi aplikasi", value: "tidak dilaporkan" },
                    { term: "Sambungan terakhir", value: formatDateTime(device.last_ticket_at), data: true },
                  ]}
                  // A revoked computer is already cut off until it connects again, which
                  // clears the label and brings the button back.
                  action={
                    revoked ? undefined : (
                      <RevokeButton
                        label={`Cabut komputer ${short}`}
                        onClick={() =>
                          onRevoke({
                            title: "Cabut komputer ini dari relay?",
                            body: (
                              <>
                                <p>
                                  Komputer <code>{short}</code> diputus dari relay.
                                </p>
                                <p>
                                  Ini bukan blokir. Kalau ahcut di komputer itu sedang tersambung ke
                                  relay, ia mematikan relay-nya sendiri sampai sakelar Cloud relay di
                                  sana dinyalakan lagi. Kalau saat ini tidak tersambung — ahcut-nya
                                  tertutup, komputernya tidur, atau jaringannya putus — ia tersambung
                                  lagi sendiri begitu bisa, selama masih masuk ke akunmu. Cara
                                  memutusnya permanen ada di catatan di bawah daftar komputer.
                                </p>
                              </>
                            ),
                            confirmLabel: "Cabut komputer",
                            done: `Komputer ${short} dicabut dari relay.`,
                            run: () => revokeMcpRelayDevice(device.device_id),
                          })
                        }
                      />
                    )
                  }
                />
              );
            }}
          />
          <Alert title="Mencabut komputer bukan blokir">
            Cabut memutus komputer itu dari relay, tapi selama ahcut di sana masih masuk ke akunmu, ia
            bisa tersambung lagi: yang sedang tersambung mematikan relay-nya sendiri sampai sakelar{" "}
            <strong>Cloud relay</strong> dinyalakan lagi, sedangkan yang saat itu tidak tersambung —
            ahcut-nya tertutup, komputernya tidur, atau jaringannya putus — tersambung lagi sendiri
            begitu bisa. Untuk menghentikannya, matikan sakelar
            itu di aplikasi ahcut di komputer tersebut, atau tekan <strong>Sign out</strong> di sana.
            Kalau komputernya tidak di tanganmu, pilih{" "}
            <strong>Keluar dari semua perangkat</strong> di bagian Keamanan halaman{" "}
            <Link to="/">Akun</Link>: itu mengakhiri semua sesi login sekaligus mencabut semua akses
            di halaman ini. Setelah masuk lagi, cek daftar ini: kalau komputer itu muncul tanpa label
            Dicabut, cabut sekali lagi.
          </Alert>
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------- Revoke all

function RevokeAllCard({ access, onRevoke }: { access: AvailableAccess; onRevoke: OnRevoke }) {
  // Counts come from the lists on screen; a list that failed to load is named
  // without one rather than guessed.
  const clients = access.grants.ok
    ? `${access.grants.value.length} klien yang terhubung`
    : "semua klien yang terhubung";
  const keys = access.keys.ok ? `${access.keys.value.length} kunci API` : "semua kunci API";

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cabut semua akses</CardTitle>
      </CardHeader>
      <p className="text-sm text-fg-1">
        Semua klien yang terhubung, semua kunci API, dan semua komputer di atas dicabut sekaligus. Pakai
        ini kalau kamu curiga ada akses yang bukan milikmu.
      </p>
      <p className="text-sm text-fg-1">
        Akunmu tidak ikut keluar dari mana pun. Untuk mengakhiri semua sesi login sekaligus
        mencabut semua akses ini, pakai <strong>Keluar dari semua perangkat</strong> di bagian
        Keamanan halaman <Link to="/">Akun</Link>.
      </p>
      <div>
        <Button
          variant="danger"
          onClick={() =>
            onRevoke({
              title: "Cabut semua akses relay?",
              body: (
                <>
                  <p>
                    Ini mencabut {clients}, {keys}, dan setiap komputer yang tersambung ke relay.
                    Klien harus meminta izinmu lagi dan kunci perlu dibuat ulang. Komputer yang sedang
                    tersambung mematikan relay-nya sendiri; yang saat itu tidak tersambung (ahcut-nya
                    tertutup, komputernya tidur, atau jaringannya putus) dan masih masuk ke akunmu
                    tersambung lagi sendiri begitu bisa, tapi tidak ada klien atau kunci yang bisa
                    memakainya sampai kamu memberi akses lagi.
                  </p>
                  <p>
                    <strong className="font-medium text-fg-0">Tidak bisa dibatalkan.</strong> Sesi
                    login akunmu tidak ikut berakhir.
                  </p>
                </>
              ),
              confirmLabel: "Cabut semua",
              done: "Semua akses relay dicabut.",
              run: revokeAllMcpAccess,
              all: true,
            })
          }
        >
          <Glyph name="unlink" />
          Cabut semua akses
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- Row kit

interface Fact {
  term: string;
  value: ReactNode;
  /** Set the value in the data face: dates, ids, versions (ui/DESIGN.md §4). */
  data?: boolean;
}

function lastUsedFact(iso: string | null): Fact {
  return iso
    ? { term: "Terakhir dipakai", value: formatDateTime(iso), data: true }
    : { term: "Terakhir dipakai", value: "belum pernah" };
}

/** One list, from a settled read: its rows, a muted empty line, or its own
 *  inline error with a retry. Not announced: one list failing leaves the rest of
 *  the page usable (States.tsx). */
function SettledList<T>({
  list,
  label,
  empty,
  onRetry,
  renderItem,
}: {
  list: Settled<T[]>;
  label: string;
  empty: string;
  onRetry: () => void;
  renderItem: (item: T) => ReactNode;
}) {
  if (!list.ok) return <ErrorState variant="inline" error={list.error} onRetry={onRetry} />;
  if (list.value.length === 0) return <p className="text-sm text-fg-2">{empty}</p>;
  return (
    <ul aria-label={label} className="divide-y divide-line border-y border-line">
      {list.value.map((item) => renderItem(item))}
    </ul>
  );
}

/** A row: name and state first, facts under it, the action on the right from 640px. */
function AccessRow({
  title,
  badge,
  detail,
  facts,
  action,
}: {
  title: ReactNode;
  badge?: ReactNode;
  detail?: ReactNode;
  facts: readonly Fact[];
  action?: ReactNode;
}) {
  return (
    <li className="flex flex-col gap-3 py-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
      <div className="flex min-w-0 flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="min-w-0 font-medium break-words text-fg-0">{title}</span>
          {badge}
        </div>
        {detail}
        <dl className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
          {facts.map((fact) => (
            <div key={fact.term} className="flex min-w-0 flex-wrap gap-x-1.5">
              <dt className="text-fg-2">{fact.term}</dt>
              <dd className={cn("min-w-0 text-fg-1", fact.data && [dataClass, "wrap-anywhere"])}>
                {fact.value}
              </dd>
            </div>
          ))}
        </dl>
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </li>
  );
}

/** "Cabut" on a row. The visible word opens the accessible name (WCAG 2.5.3);
 *  the rest tells a screen-reader user which row it belongs to. */
function RevokeButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button variant="danger" size="sm" aria-label={label} onClick={onClick}>
      Cabut
    </Button>
  );
}
